import { useEffect, useRef, useState } from 'react'
import {
  getLoginURL,
  geto365Token,
  refreshToken,
} from '../../../../api/commonApi'
import {
  getTeamsAuthToken,
  initializeTeamsRuntime,
} from '../utils/teamsRuntime'
import { login } from '../utils/login'

import {
  LOCAL_AUTH_STORAGE_KEYS,
  LoginType,
} from '../constants'
import { loginProp } from '../type'
import { isTeamsApp } from '../../../constants'
import { notifyError } from '@/matteroomTab/Notification/NotificationContext'
import { useLoadingStore } from '@/stores/useLoadingStore'
import { persistLoginSession } from '../utils/session'
import {
  clearStoredAccessToken,
  getStoredAccessToken,
  setStoredAccessToken,
  setStoredRefreshToken,
} from '../utils/storage'

type useAuthOptions = {
  onLoginSuccess: () => void
  onNoAccountChange: (open: boolean) => void
}

type LoginActionOptions = {
  useGlobalLoading?: boolean
}

export const useAuth = ({
  onLoginSuccess,
  onNoAccountChange,
}: useAuthOptions) => {
  const [logined, setLogined] = useState(false)

  const { setLoading } = useLoadingStore()

  const refreshTokenTimerRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  )

  const handleWebLogin = async (
    { loginEmail, isRemember, loginType = LoginType.o365 }: loginProp,
    { useGlobalLoading = true }: LoginActionOptions = {}
  ) => {
    const email = loginEmail?.trim()
    if (!email) return notifyError('Please enter your email address.')

    if (useGlobalLoading) setLoading(true)
    try {
      const loginRes = await getLoginURL(email, undefined, loginType)
      if (loginRes.code == 200000) {
        const loginUrl = loginRes.data.login_url || ''
        login(email, loginUrl, isRemember)
      }
      if (loginRes.code == 401003) {
        onNoAccountChange(true)
        clearStoredAccessToken()
      }
    } finally {
      if (useGlobalLoading) setLoading(false)
    }
  }

  const handleo365Login = async ({
    useGlobalLoading = true,
  }: LoginActionOptions = {}) => {
    if (useGlobalLoading) setLoading(true)
    try {
      const teamsAuthToken = await getTeamsAuthToken()
      if (!teamsAuthToken) {
        notifyError('Unable to get Teams authentication token.')
        return
      }

      const o365TokenRes = await geto365Token(teamsAuthToken)

      if (o365TokenRes.code == 200000) {
        persistLoginSession(o365TokenRes.data)
        window.location.reload()
      }

      if (o365TokenRes.code == 401004) {
        notifyError(o365TokenRes.message)
      }

      if (o365TokenRes.code == 404001) {
        onNoAccountChange(true)
      }
    } catch (error) {
      console.error('Teams O365 login failed:', error)
      notifyError('Teams O365 login failed:', error)
    } finally {
      if (useGlobalLoading) setLoading(false)
    }
  }

  const handleLogin = async (
    prop: loginProp,
    options: LoginActionOptions = {}
  ) => {
    if (!isTeamsApp) return handleWebLogin(prop, options)
    const runtime = await initializeTeamsRuntime()
    if (!runtime.inTeams) return
    onNoAccountChange(false)
    await handleo365Login(options)
  }

  const doRefreshToken = async () => {
    try {
      const res = await refreshToken()
      if (res?.token) {
        setStoredAccessToken(res.token)
      }
      if (res?.refresh_token) {
        setStoredRefreshToken(res.refresh_token)
      }
    } catch (e) {
      console.error('refreshToken failed:', e)
    }
  }

  useEffect(() => {
    if (!logined) return
    if (refreshTokenTimerRef.current) return

    refreshTokenTimerRef.current = setInterval(
      () => {
        doRefreshToken()
      },
      24 * 60 * 60 * 1000
    )

    return () => {
      if (refreshTokenTimerRef.current) {
        clearInterval(refreshTokenTimerRef.current)
        refreshTokenTimerRef.current = null
      }
    }
  }, [logined])

  useEffect(() => {
    if (isTeamsApp) initializeTeamsRuntime()
  }, [isTeamsApp])

  useEffect(() => {
    if (
      getStoredAccessToken() &&
      localStorage.getItem(LOCAL_AUTH_STORAGE_KEYS.user)
    ) {
      setLogined(true)
      onLoginSuccess()
    } else if (isTeamsApp) {
      handleLogin({ isRemember: true })
    }
  }, [])

  return {
    logined,
    handleLogin,
  }
}
